import { useContext, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { configApi } from '../../../assets/api/services/configApi';
import { TranslationContext } from '../../../assets/contexts/translationContext';
import { RightArrowIcon, SearchIcon } from '../../../assets/icons/icons';
import { triggerImpactHaptic } from '../../../assets/utils/hapticFeedback';
import ScreenHeader from '../../ScreenHeader/ScreenHeader';
import '../profileSubpage.css';
import './ProfileHelpSupport.css';

const INITIAL_FAQ_COUNT = 6;

function ProfileHelpSupport() {
  const navigate = useNavigate();
  const { profile } = useContext(TranslationContext);
  const { help } = profile;
  const [openQuestionId, setOpenQuestionId] = useState(null);
  const [query, setQuery] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [isOpeningSupport, setIsOpeningSupport] = useState(false);
  const normalizedQuery = query.trim().toLocaleLowerCase();
  const filteredFaqs = useMemo(() => {
    if (!normalizedQuery) {
      return help.faqs;
    }

    return help.faqs.filter((item) => (
      `${item.question} ${item.answer}`.toLocaleLowerCase().includes(normalizedQuery)
    ));
  }, [help.faqs, normalizedQuery]);
  const visibleFaqs = normalizedQuery || showAll
    ? filteredFaqs
    : filteredFaqs.slice(0, INITIAL_FAQ_COUNT);
  const toggleQuestion = (questionId) => {
    triggerImpactHaptic('light');
    setOpenQuestionId((currentQuestionId) => (
      questionId === currentQuestionId ? null : questionId
    ));
  };
  const handleContactClick = async () => {
    if (isOpeningSupport) {
      return;
    }

    triggerImpactHaptic('light');
    setIsOpeningSupport(true);

    try {
      const config = await configApi.get();
      const username = typeof config?.tg_support_username === 'string'
        ? config.tg_support_username.trim().replace(/^@/, '')
        : '';
      const webApp = window.Telegram?.WebApp;

      if (!/^[a-z][a-z0-9_]{4,31}$/i.test(username)) {
        throw new Error('Invalid Telegram support username');
      }

      if (typeof webApp?.openTelegramLink !== 'function') {
        throw new Error('Telegram link API is unavailable');
      }

      webApp.openTelegramLink(`https://t.me/${username}`);
    } catch {
      toast.error(help.contactFailed);
    } finally {
      setIsOpeningSupport(false);
    }
  };
  const handleSearchChange = (event) => {
    setQuery(event.target.value);
    setOpenQuestionId(null);
  };
  const handleShowAll = () => {
    triggerImpactHaptic('light');
    setShowAll(true);
  };

  return (
    <section className="profile-subpage profile-help">
      <div className="profile-subpage__content">
        <ScreenHeader title={profile.helpSupport} onBack={() => navigate('/profile')} />

        <p className="profile-help__intro">
          {help.intro}
        </p>

        <section className="profile-help__support">
          <h2 className="profile-help__support-title">{help.supportTitle}</h2>
          <p className="profile-help__support-text">{help.supportText}</p>
          <button
            type="button"
            className="profile-help__contact"
            onClick={handleContactClick}
            disabled={isOpeningSupport}
            aria-busy={isOpeningSupport}
          >
            <span>{isOpeningSupport ? help.contactOpening : help.contact}</span>
            <RightArrowIcon className="profile-help__contact-icon" aria-hidden="true" />
          </button>
        </section>

        <label className="profile-help__search">
          <SearchIcon className="profile-help__search-icon" />
          <input
            type="search"
            className="profile-help__search-input"
            value={query}
            placeholder={help.searchPlaceholder}
            onChange={handleSearchChange}
          />
        </label>

        <h2 className="profile-help__section-title">{help.faq}</h2>

        <div className="profile-help__faq">
          {visibleFaqs.map((item, index) => {
            const isOpen = item.id === openQuestionId;

            return (
              <div key={item.id} className="profile-help__faq-item">
                <button
                  type="button"
                  className="profile-help__question"
                  aria-expanded={isOpen}
                  aria-controls={`help-answer-${item.id}`}
                  onClick={() => toggleQuestion(item.id)}
                >
                  <span className="profile-help__number">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="profile-help__question-text">{item.question}</span>
                  <motion.span
                    className="profile-help__plus"
                    aria-hidden="true"
                    animate={{ rotate: isOpen ? 45 : 0 }}
                    transition={{ duration: 0.18, ease: 'easeOut' }}
                  >
                    +
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      id={`help-answer-${item.id}`}
                      className="profile-help__answer-wrap"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <p className="profile-help__answer">{item.answer}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {visibleFaqs.length === 0 ? (
          <p className="profile-help__no-results">{help.noResults}</p>
        ) : null}

        {!normalizedQuery && !showAll && filteredFaqs.length > INITIAL_FAQ_COUNT ? (
          <button type="button" className="profile-help__show-all" onClick={handleShowAll}>
            {help.showAll}
          </button>
        ) : null}
      </div>
    </section>
  );
}

export default ProfileHelpSupport;
